import './addNotification.scss';
import { useState } from 'react';
import Loader from 'components/loader';
import InputField from 'components/inputField';
import AudienceDropdown from 'pages/Notifications/notificationList/audienceDropdown';
import Button from 'components/button';
import FormDisplayGroup from 'components/formDisplayGroup';
import { toast } from 'react-toastify';
import { toastOptions } from 'helpers/colorConstants';
import { useSelectOptionsQuery } from 'hooks/queries/use-select.queries';
import {
	useCreateNotificationMutation,
	useSendTestSmsMutation,
} from 'hooks/mutations/use-notificacion.mutations';
import TextArea from 'components/inputs/TextArea';
import { isValidPhoneNumber } from 'services/validations';
import {
	deformatPhoneNumber,
	formattedPhoneNumber,
} from 'helpers/formattedPhoneNumber';
import {
	AsYouType,
	isValidNumber as isValidNumberLibJs,
} from 'libphonenumber-js';
import {
	MAX_ALLOWED_TITLE_LENGTH,
	TITLE_ERRORS,
	MESSAGE_ERRORS,
	TEST_PHONE_NUMBER_ERRORS,
	MAX_ALLOWED_MESSAGE_SEGMENTS,
} from 'constants/validations';
import { analyzeSms, getMaxMessageLength } from 'helpers/analyzeSms';
import { ALL_MEMBERS_LABEL, AUDIENCE_TYPES } from 'helpers/notificationDelivery';

const AUDIENCE_REQUIRED_ERROR = 'Select at least one venue';

const getMessageError = (message) => {
	if (!message) return MESSAGE_ERRORS.MESSAGE_REQUIRED;

	const analysis = analyzeSms(message);
	const maxLength = getMaxMessageLength(analysis.encoding);

	if (message.length > maxLength) {
		return MESSAGE_ERRORS.MESSAGE_LENGTH_EXCEEDED(maxLength);
	}
	if (analysis.segments > MAX_ALLOWED_MESSAGE_SEGMENTS) {
		return MESSAGE_ERRORS.MESSAGE_SEGMENTS_EXCEEDED(analysis);
	}
	return '';
};

export const AddNotification = ({
	onRequestClose,
	onSent, // receives the id of the queued notification
	...rest
}) => {
	const [notificationTitle, setNotificationTitle] = useState('');
	const [notificationTitleError, setNotificationTitleError] = useState('');

	const [notificationMessage, setNotificationMessage] = useState('');
	const [notificationMessageError, setNotificationMessageError] = useState('');

	const [audienceType, setAudienceType] = useState(AUDIENCE_TYPES.VENUES);
	const [notificationAudience, setNotificationAudience] = useState([]);
	const [openDropdown, setOpenDropdown] = useState(null);

	const [isConfirming, setIsConfirming] = useState(false);

	const [testModeEnabled, setTestModeEnabled] = useState(false);
	const [testPhoneNumber, setTestPhoneNumber] = useState('+1 (555) 000-0000');
	const [testPhoneNumberError, setTestPhoneNumberError] = useState(false);
	const [testSmsResponse, setTestSmsResponse] = useState(null);

	const [smsInfo, setSmsInfo] = useState(null);

	const mutation = useCreateNotificationMutation(null);
	const { data: selectOptions } = useSelectOptionsQuery();
	const testSmsMutation = useSendTestSmsMutation((response) => {
		setTestSmsResponse(response);
	});

	const toggleDropdown = () => {
		setOpenDropdown(!openDropdown);
	};

	const validateNotificationTitle = () => {
		if (!notificationTitle) {
			setNotificationTitleError(TITLE_ERRORS.TITLE_REQUIRED);
		} else {
			setNotificationTitleError('');
		}
	};

	// Receives the value to validate: the state is not updated yet when it
	// runs from onChange.
	const validateNotificationMessage = (message = notificationMessage) => {
		const error = getMessageError(message);
		setNotificationMessageError(error);
		return error;
	};

	const isAllMembers = audienceType === AUDIENCE_TYPES.ALL;
	const isAudienceMissing = !isAllMembers && notificationAudience.length === 0;

	// Validates the form and shows the confirmation step
	const onHandle = async (e) => {
		e.preventDefault();

		if (notificationTitleError) {
			toast.error(notificationTitleError, toastOptions);
			return;
		}

		const messageError = validateNotificationMessage();
		if (messageError) {
			toast.error(messageError, toastOptions);
			return;
		}

		if (isAudienceMissing) {
			toast.error(AUDIENCE_REQUIRED_ERROR, toastOptions);
			return;
		}

		setOpenDropdown(null);
		setIsConfirming(true);
	};

	const onConfirmSend = (e) => {
		e.preventDefault();

		mutation.mutate(
			{
				title: notificationTitle,
				message: notificationMessage,
				audience_type: audienceType,
				audience: notificationAudience,
			},
			{
				onSuccess: (response) => {
					// Errors are shown by the mutation: go back to the form
					if (!response || response.error) {
						setIsConfirming(false);
						return;
					}

					// The send continues in the background: follow it in the detail
					if (onSent) {
						onSent(response.notification_id);
					} else {
						onRequestClose();
					}
				},
			},
		);
	};

	const onHandleTestSms = async (e) => {
		e.preventDefault();

		if (!testPhoneNumber) {
			toast.error(
				TEST_PHONE_NUMBER_ERRORS.TEST_PHONE_NUMBER_REQUIRED,
				toastOptions,
			);
			return;
		}

		if (!notificationTitle) {
			toast.error(TITLE_ERRORS.TITLE_REQUIRED, toastOptions);
			return;
		}

		if (!notificationMessage) {
			toast.error(MESSAGE_ERRORS.MESSAGE_REQUIRED, toastOptions);
			return;
		}

		testSmsMutation.mutate({
			title: notificationTitle,
			message: notificationMessage,
			phone_number: deformatPhoneNumber(formattedPhoneNumber(testPhoneNumber)),
		});
	};

	const onChangeTestPhoneNumber = (e) => {
		let fieldValue = e.target.value;
		// Check if the field is cleared
		if (fieldValue === '') {
			// Handle the case when the field is cleared
			fieldValue = '';
		} else {
			// Try to parse the input as a phone number
			const parsedNumber = new AsYouType('US').input(fieldValue);

			// Check if the parsed number is valid
			if (isValidNumberLibJs(parsedNumber, 'US')) {
				fieldValue = parsedNumber;
			}
		}

		setTestPhoneNumber(fieldValue);
		setTestPhoneNumberError(
			!isValidPhoneNumber(deformatPhoneNumber(fieldValue)),
		);
	};

	const onChangeNotificationMessage = (e) => {
		const newValue = e.target.value;
		const analysis = analyzeSms(newValue);

		if (newValue.length > getMaxMessageLength(analysis.encoding)) return;

		setNotificationMessage(newValue);
		validateNotificationMessage(newValue);
		setSmsInfo(analysis);
	};

	const onSmartEncodeMessage = (cleanedMessage) => {
		setNotificationMessage(cleanedMessage);

		const analysis = analyzeSms(cleanedMessage);
		setSmsInfo(analysis);
		validateNotificationMessage(cleanedMessage);
	};

	const maxAllowedMessageLength = getMaxMessageLength(
		smsInfo?.encoding ?? 'GSM-7',
	);

	const onChangeNotificationTitle = (e) => {
		if (e.target.value.length > MAX_ALLOWED_TITLE_LENGTH) return;
		setNotificationTitle(e.target.value);
		validateNotificationTitle();
	};

	const onChangeAudienceType = (e) => {
		setAudienceType(e.target.value);
		setOpenDropdown(null);
	};

	const renderConfirmation = () => {
		const venues = selectOptions?.venues || [];
		const selectedVenues = notificationAudience.map((venueId) => {
			const venue = venues.find((option) => option.value === venueId);
			return { id: venueId, name: venue ? venue.label : `Venue ${venueId}` };
		});
		const analysis = analyzeSms(notificationMessage);

		return (
			<form className="AddNotification__form">
				<div className="AddNotification__form__section">
					<div className="AddNotification__confirm">
						<h4 className="AddNotification__confirm__title">
							Review and confirm
						</h4>
						<FormDisplayGroup
							fieldLabel="Title"
							fieldValue={notificationTitle}
						/>
						<FormDisplayGroup
							fieldLabel="Message"
							fieldValue={notificationMessage}
							classValue="AddNotification__confirm__message"
						/>
						{isAllMembers ? (
							<FormDisplayGroup
								fieldLabel="Send to"
								fieldValue={ALL_MEMBERS_LABEL}
							/>
						) : (
							<FormDisplayGroup
								fieldLabel="Send to"
								fieldValues={selectedVenues}
							/>
						)}
						<FormDisplayGroup
							fieldLabel="Segments per message"
							fieldValue={`${analysis.segments} (${analysis.encoding})`}
						/>
						<div
							className={
								'AddNotification__confirm__warning' +
								(isAllMembers
									? ' AddNotification__confirm__warning--prominent'
									: '')
							}
							role="alert">
							{isAllMembers && (
								<strong>
									This SMS will be sent to every active member.
								</strong>
							)}
							<span>
								Once the send starts it cannot be canceled. The exact number
								of recipients is shown after the send is queued.
							</span>
						</div>
					</div>
				</div>
				<div className="AddNotification__form__section buttons_section">
					<Button
						buttonWrapperClass="cancel"
						displayLabel="Back"
						color="black"
						size="full"
						type="button"
						onClick={() => setIsConfirming(false)}
					/>
					<Button
						buttonWrapperClass="savePushNotification"
						displayLabel={
							isAllMembers ? 'Send to all active members' : 'Confirm and send'
						}
						color="white"
						size="full"
						type="button"
						onClick={onConfirmSend}
						disabled={mutation.isPending}
					/>
				</div>
			</form>
		);
	};

	const isBusy = mutation.isPending || testSmsMutation.isPending;

	return (
		<div className="AddNotification" {...rest}>
			{isBusy && <Loader />}
			{!isBusy && isConfirming && renderConfirmation()}
			{!isBusy && !isConfirming && (
				<form className="AddNotification__form">
					<div className="AddNotification__form__section">
						<InputField
							id="notificationTitle"
							name="notificationTitle"
							displayLabel="Notification Title*"
							onChange={onChangeNotificationTitle}
							onBlur={() => validateNotificationTitle()}
							value={notificationTitle}
							inputError={notificationTitleError}
							placeholder="Enter notification title"
						/>
						<TextArea
							id="notificationMessage"
							name="notificationMessage"
							displayLabel="Message*"
							onChange={onChangeNotificationMessage}
							onBlur={() => validateNotificationMessage()}
							value={notificationMessage}
							inputError={notificationMessageError}
							maxLength={maxAllowedMessageLength}
							placeholder={`Enter notification message (max ${maxAllowedMessageLength} characters)`}
							showSmsSegmentInfo={smsInfo}
							onSmartEncode={onSmartEncodeMessage}
						/>

						<fieldset className="AddNotification__form__section__audience">
							<legend className="AddNotification__form__section__audience__label">
								Send to*
							</legend>
							<div className="AddNotification__form__section__audience__options">
								<label className="AddNotification__form__section__audience__option">
									<input
										type="radio"
										name="audienceType"
										value={AUDIENCE_TYPES.VENUES}
										checked={audienceType === AUDIENCE_TYPES.VENUES}
										onChange={onChangeAudienceType}
									/>
									<span>Specific venues</span>
								</label>
								<label className="AddNotification__form__section__audience__option">
									<input
										type="radio"
										name="audienceType"
										value={AUDIENCE_TYPES.ALL}
										checked={isAllMembers}
										onChange={onChangeAudienceType}
									/>
									<span>{ALL_MEMBERS_LABEL}</span>
								</label>
							</div>
						</fieldset>
						{!isAllMembers && (
							<div className="AddNotification__form__section__venues">
								<AudienceDropdown
									displayLabel="Venues*"
									emptyLabel="Select venues"
									currentAudience={notificationAudience}
									setCurrentAudience={setNotificationAudience}
									isOpen={openDropdown}
									onToggle={toggleDropdown}
								/>
								{isAudienceMissing && (
									<span className="AddNotification__form__section__venues__error">
										{AUDIENCE_REQUIRED_ERROR}
									</span>
								)}
							</div>
						)}
						<div className="AddNotification__form__section__test-mode">
							<label className="AddNotification__form__section__test-mode__checkbox-label">
								<input
									type="checkbox"
									className="AddNotification__form__section__test-mode__checkbox"
									checked={testModeEnabled}
									onChange={(e) => {
										setTestModeEnabled(e.target.checked);
										if (!e.target.checked) {
											setTestSmsResponse(null);
										}
									}}
								/>
								<span>Enable test mode</span>
							</label>
						</div>
						{testModeEnabled && (
							<div className="AddNotification__form__section__test-section">
								<InputField
									id="testPhoneNumber"
									name="testPhoneNumber"
									displayLabel="Test Phone Number*"
									value={testPhoneNumber}
									placeholder="+1 (555) 000-0000"
									onChange={onChangeTestPhoneNumber}
									inputError={testPhoneNumberError}
								/>
								<Button
									buttonWrapperClass="sendTestMessage"
									displayLabel="Send Test Message"
									color="black"
									size="full"
									onClick={onHandleTestSms}
									disabled={
										!testPhoneNumber ||
										!notificationMessage ||
										!notificationTitle ||
										testSmsMutation.isPending
									}
								/>
								{testSmsResponse && !testSmsResponse.error && (
									<div className="AddNotification__form__section__test-response">
										<div className="AddNotification__form__section__test-response__content">
											{testSmsResponse.status && (
												<div className="AddNotification__form__section__test-response__item">
													<span className="AddNotification__form__section__test-response__label">
														Status:
													</span>
													<span className="AddNotification__form__section__test-response__value">
														{testSmsResponse.status}
													</span>
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						)}
					</div>
					<div className="AddNotification__form__section buttons_section">
						<Button
							buttonWrapperClass="cancel"
							displayLabel="Cancel"
							color="black"
							size="full"
							onClick={onRequestClose}
						/>
						<Button
							buttonWrapperClass="savePushNotification"
							displayLabel="Send SMS Notification"
							color="white"
							size="full"
							onClick={onHandle}
							disabled={
								!notificationMessage ||
								!notificationTitle ||
								notificationTitleError ||
								notificationMessageError ||
								isAudienceMissing ||
								mutation.isPending ||
								testModeEnabled
							}
						/>
					</div>
				</form>
			)}
		</div>
	);
};
